<template>
<div>
  <!-- Backups -->
  <h1>Backup</h1>
  <el-button
    @click="backup()"
    >
    Download database backup
  </el-button>

  <el-delimiter />

  <div v-if="enableBackupRestore">
    <h1>Restore backup</h1>
    <el-message
      :danger="true"
      >
      CAUTION! This will overwrite the database.
    </el-message>
    <br>
    <el-file-drop
      accept=".txt"
      @select="handleFiles"
      />

    <el-button
      :danger="true"
      @click="restoreBackup"
      :disabled="restoredData === null"
      :margin-top="true"
      >
      Restore backup
    </el-button>
  <el-delimiter />
  </div>

  <!-- Server API endpoints -->
  <h1>Test server API</h1>
  <p><b>NOTE:</b> Do not close the browser before tests are fully completed. They may take up to 30 sec to complete.</p>
  <el-button
    :danger="true"
    :disabled="isTesting"
    @click="runAllTests()"
    >
    Run server API endpoint tests
  </el-button>
  <el-inline
    v-for="(test, index) in tests"
    :key="`tests_${index}`"
    :margin-top="false"
    :space-between="true"
    >
    <h3>{{test.title}}</h3>
    <el-tag
      :success="test.status === 'success'"
      :danger="test.status === 'failed'"
      :info="test.status === 'pending'"
      >
      {{test.status}}
    </el-tag>
    <el-loader v-if="test.status === 'pending'" />
  </el-inline>

  <el-delimiter />
  <h1>Danger zone below, use with caution.</h1>
  <p>Too many API requests to 3rd party can result in exceeding limit quota and temporarily disabling the integration.</p>

  <!-- Mail Chimp -->
  <el-button
    :danger="true"
    @click="runEmailTests()"
    >
    Run MailChimp integration tests
  </el-button>
  <el-inline
    v-for="(test, index) in emailTests"
    :key="`emailTests_${index}`"
    :margin-top="false"
    :space-between="true"
    >
    <h3>{{test.title}}</h3>
    <el-tag
      :success="test.status === 'success'"
      :danger="test.status === 'failed'"
      :info="test.status === 'pending'"
      >
      {{test.status}}
    </el-tag>
    <el-loader v-if="test.status === 'pending'" />
  </el-inline>

  <!-- Clear Mail Chimp -->
  <el-delimiter />
  <el-button
    :danger="true"
    @click="runClearEmailTests()"
    >
    Clean up Mail Chimp test data
  </el-button>
  <el-inline
    v-for="(test, index) in clearEmailTests"
    :key="`clearEmailTests${index}`"
    :margin-top="false"
    :space-between="true"
    >
    <h3>{{test.title}}</h3>
    <el-tag
      :success="test.status === 'success'"
      :danger="test.status === 'failed'"
      :info="test.status === 'pending'"
      >
      {{test.status}}
    </el-tag>
    <el-loader v-if="test.status === 'pending'" />
  </el-inline>

</div>
</template>
<script>
import api from 'services/api.js'
import emailApi from 'services/emailApi.js'
import firestore from 'services/firestore.js'
import ElFileDrop from 'components/elements/ElFileDrop.vue'
import devMode from '@/devMode.js'

import logScope from 'log-scope'
const log = logScope('ServerStatus')

export default {
  components: { ElFileDrop },
  data () {
    return {
      isTesting: false,
      enableBackupRestore: devMode.enableBackupRestore,
      restoredData: null,
      tests: [{
        title: 'Email subscription',
        status: 'unknown',
        method: 'signup'
      }, {
        title: 'Get coupon price',
        status: 'unknown',
        method: 'getCouponPrice'
      }, {
        title: 'Purchase (inc. partner code)',
        status: 'unknown',
        method: 'purchase'
      }, {
        title: 'Purchase with coupon code',
        status: 'unknown',
        method: 'purchaseWithCoupon'
      }, {
        title: 'HeadQ settings (production)',
        status: 'unknown',
        method: 'headQSettingsProduction'
      }, {
        title: 'HeadQ settings (development)',
        status: 'unknown',
        method: 'headQSettingsDevelopment'
      }, {
        title: 'HeadQ firmware (production)',
        status: 'unknown',
        method: 'headQFirmwareProduction'
      }, {
        title: 'HeadQ firmware (development)',
        status: 'unknown',
        method: 'headQFirmwareDevelopment'
      }, {
        title: 'Clear test purchases',
        status: 'unknown',
        method: 'clearTestPurchases',
        delay: true
      }, {
        title: 'Clear test subscibers',
        status: 'unknown',
        method: 'clearTestSubscriptions',
        delay: true
      }],
      emailTests: [{
        title: 'MailChimp signup API',
        status: 'unknown',
        method: 'mailChimpNewSubscriber'
      }],
      clearEmailTests: [{
        title: 'Clear MailChimp test subscriber',
        status: 'unknown',
        method: 'mailChimpClearTestSubscriptions',
        delay: true
      }]
    }
  },
  methods: {
    backup () {
      firestore.downloadBackup()
    },
    async runAllTests () {
      this.isTesting = true

      // Run these first
      await Promise.all(this.tests
        .filter(test => test.delay !== true)
        .map(async (test) => {
          test.status = 'pending'

          try {
            await this[test.method]()
            test.status = 'success'
          } catch (err) {
            log.error('Failed server status test: ' + test.title, err)
            test.status = 'failed'
          }
        }))

      // Second batch (cleanup)
      await Promise.all(this.tests
        .filter(test => test.delay === true)
        .map(async (test) => {
          test.status = 'pending'

          try {
            await this[test.method]()
            test.status = 'success'
          } catch (err) {
            log.error('Failed server status test: ' + test.title, err)
            test.status = 'failed'
          }
        }))

      this.isTesting = false
    },
    async runEmailTests () {
      this.isTesting = true

      await Promise.all(this.emailTests
        .map(async test => {
          test.status = 'pending'

          try {
            await this[test.method]()
            test.status = 'success'
          } catch (err) {
            log.error('Failed server status test: ' + test.title, err)
            test.status = 'failed'
          }
        }))
    },
    async runClearEmailTests () {
      this.isTesting = true

      await Promise.all(this.clearEmailTests
        .map(async test => {
          test.status = 'pending'

          try {
            await this[test.method]()
            test.status = 'success'
          } catch (err) {
            log.error('Failed server status test: ' + test.title, err)
            test.status = 'failed'
          }
        }))
    },
    signup () {
      return api.signup('test@rideq.se')
    },
    mailChimpNewSubscriber () {
      return emailApi.newSubscriber('test@rideq.se')
    },
    purchase () {
      return api.purchase({
        email: 'test@rideq.se',
        firstName: 'TEST',
        lastName: 'Testsson',
        address: 'Test street',
        city: 'Testington',
        postCode: '123 45',
        phoneNumber: '070 123 45 67',
        quantity: 2,
        partnerCode: 'test'
      })
    },
    purchaseWithCoupon () {
      return api.purchase({
        email: 'test@rideq.se',
        firstName: 'TEST',
        lastName: 'Testsson',
        address: 'Test street',
        city: 'Testington',
        postCode: '123 45',
        phoneNumber: '070 123 45 67',
        couponCode: 'test',
        quantity: 2,
        partnerCode: ''
      })
    },
    getCouponPrice () {
      return api.getCouponPrice('test')
    },
    clearTestPurchases () {
      return api.clearTestPurchases()
    },
    clearTestSubscriptions () {
      return api.clearTestSubscriptions()
    },
    mailChimpClearTestSubscriptions () {
      return emailApi.clearTestSubscriptions()
    },
    async headQSettingsDevelopment () {
      try {
        const res = await api.headQSettings('development')
        const isOk = this.configCheck(res.data)

        return isOk ? Promise.resolve() : Promise.reject(new Error('Invalid config'))
      } catch (err) {
        return Promise.reject(err)
      }
    },
    async headQSettingsProduction () {
      try {
        const res = await api.headQSettings('production')
        const isOk = this.configCheck(res.data)

        return isOk ? Promise.resolve() : Promise.reject(new Error('Invalid config'))
      } catch (err) {
        return Promise.reject(err)
      }
    },
    async headQFirmwareProduction () {
      const profile = 'production'

      try {
        const res = await api.headQFirmware(profile)
        const downloadUrl = res.data.downloadUrl

        if (!downloadUrl || downloadUrl === '') {
          return Promise.reject(new Error('No firmware for profile: ' + profile))
        } else {
          return Promise.resolve()
        }
      } catch (err) {
        return Promise.reject(err)
      }
    },
    async headQFirmwareDevelopment () {
      const profile = 'development'

      try {
        const res = await api.headQFirmware(profile)
        const downloadUrl = res.data.downloadUrl

        if (!downloadUrl || downloadUrl === '') {
          return Promise.reject(new Error('No firmware for profile: ' + profile))
        } else {
          return Promise.resolve()
        }
      } catch (err) {
        return Promise.reject(err)
      }
    },
    configCheck (config) {
      let isOk = true

      if (!config) {
        log.error('No config to check')
        return false
      }

      if (typeof config.firmwareVersion !== 'number') {
        log.error('Firmware version not a number', config)
        isOk = false
      }

      if (typeof config.meanLevel !== 'number') {
        log.error('Mean level not a number', config)
        isOk = false
      }

      ['deviceConfiguration', 'levelConfiguration'].forEach(type => {
        if (!config[type]) {
          isOk = false
          log.error('No config type: ' + type, config)
        } else {
          Object.entries(config[type])
            .forEach(([key, value]) => {
              if (typeof value !== 'number') {
                log.error(`Config ${type} not a number. Key: ${key}. Value: ${value}`, config)
                isOk = false
              }
            })
        }
      })

      if (!config.angles) {
        isOk = false
        log.error('No angles in config', config)
      } else {
        Object.entries(config.angles).forEach(([mode, angles]) => {
          const index = ['mode_1', 'mode_2', 'mode_3'].indexOf(mode)

          if (index === -1) {
            log.error('Invalid angle mode', config)
            isOk = false
          }

          angles.forEach(angle => {
            if (typeof angle !== 'number') {
              log.error('Angle not a number', config)
              isOk = false
            }
          })
        })
      }

      return isOk
    },
    handleFiles (files) {
      log.debug('Upload backup file', files)

      if (files.length !== 1) {
        return
      }
      const file = files[0]
      const reader = new FileReader()
      reader.onerror = error => log.danger('Could not read', error)
      reader.onload = event => {
        const data = JSON.parse(event.target.result)
        log.success('File read', data)
        log.danger('NOTE: Double check the data before restoring backup.')

        this.restoredData = data
      }
      reader.readAsText(file)
    },
    async restoreBackup () {
      log.debug('Pre restore')
      await firestore.restoreBackup(this.restoredData)
      log.success('Resore completed')
    }
  }
}
</script>
<style lang="less" scoped>

</style>
